import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listControls, listOpenAccessReviews, listReviewItems, decideReviewItem, listEvidence,
} from "@/lib/compliance.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/compliance")({
  component: ComplianceAdmin,
});

function ComplianceAdmin() {
  const listControlsFn = useServerFn(listControls);
  const listReviewsFn = useServerFn(listOpenAccessReviews);
  const listEvidenceFn = useServerFn(listEvidence);

  const { data: ctrl } = useQuery({ queryKey: ["compliance-controls"], queryFn: () => listControlsFn() });
  const { data: rev } = useQuery({ queryKey: ["compliance-reviews"], queryFn: () => listReviewsFn() });

  const [selectedControl, setSelectedControl] = useState<string>("CC7.1");
  const { data: ev } = useQuery({
    queryKey: ["compliance-evidence", selectedControl],
    queryFn: () => listEvidenceFn({ data: { controlId: selectedControl } }),
  });

  return (
    <div className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">SOC2 Compliance</h2>
        <p className="text-sm text-muted-foreground">Controls, evidence, and quarterly access reviews.</p>
      </div>
      <Tabs defaultValue="controls">
        <TabsList>
          <TabsTrigger value="controls">Controls</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
          <TabsTrigger value="reviews">Access reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="controls" className="border rounded-md mt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Control</TableHead>
                <TableHead>Family</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last reviewed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(ctrl?.controls ?? []).map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono">{c.control_id}</TableCell>
                  <TableCell>{c.family}</TableCell>
                  <TableCell>{c.description}</TableCell>
                  <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.last_reviewed_at ? new Date(c.last_reviewed_at).toLocaleDateString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="evidence" className="space-y-3 mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm">Control:</span>
            <Select value={selectedControl} onValueChange={setSelectedControl}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(ctrl?.controls ?? []).map((c) => (
                  <SelectItem key={c.control_id} value={c.control_id}>{c.control_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Captured</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Detail</TableHead>
                  <TableHead>Hash</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(ev?.evidence ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-muted-foreground">No evidence captured yet.</TableCell></TableRow>
                )}
                {(ev?.evidence ?? []).map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs">{new Date(e.captured_at).toLocaleString()}</TableCell>
                    <TableCell>{e.kind}</TableCell>
                    <TableCell className="font-mono text-xs">{e.ref}</TableCell>
                    <TableCell className="font-mono text-xs max-w-md truncate">{JSON.stringify(e.detail)}</TableCell>
                    <TableCell className="font-mono text-xs">{String(e.hash).slice(0, 12)}…</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4 mt-4">
          {(rev?.reviews ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">No open access reviews. Next quarterly review opens automatically.</p>
          )}
          {(rev?.reviews ?? []).map((r) => (
            <ReviewPanel key={r.id} reviewId={r.id} period={`${r.period_start} → ${r.period_end}`} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReviewPanel({ reviewId, period }: { reviewId: string; period: string }) {
  const qc = useQueryClient();
  const itemsFn = useServerFn(listReviewItems);
  const decideFn = useServerFn(decideReviewItem);
  const { data } = useQuery({
    queryKey: ["review-items", reviewId],
    queryFn: () => itemsFn({ data: { reviewId } }),
  });
  const decide = async (id: string, decision: "keep" | "revoke" | "change") => {
    try {
      await decideFn({ data: { itemId: id, decision } });
      qc.invalidateQueries({ queryKey: ["review-items", reviewId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };
  return (
    <div className="border rounded-md">
      <div className="px-4 py-2 border-b text-sm font-medium">Review {period}</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Decision</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data?.items ?? []).map((it) => (
            <TableRow key={it.id}>
              <TableCell className="font-mono text-xs">{it.user_id.slice(0, 8)}…</TableCell>
              <TableCell>{it.role}</TableCell>
              <TableCell><Badge variant={it.decision === "pending" ? "secondary" : "outline"}>{it.decision}</Badge></TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => decide(it.id, "keep")}>Keep</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(it.id, "change")}>Change</Button>
                  <Button size="sm" variant="destructive" onClick={() => decide(it.id, "revoke")}>Revoke</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}